import { useMemo } from 'react';
import ReactMarkdown, { Components, defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MetricOption } from '../../interfaces/MetricsInterface';
import { isImageTag } from '../../services/visualizationService';
import { isMetricTag } from '../../services/metricsService';
import { metricsOfTagParagraph } from './metricTags';
import ChatMetricPills from './ChatMetricPills';
import ChatVizImage from './ChatVizImage';

interface ChatMarkdownProps {
  text: string;
  metricOptions?: MetricOption[];
  onMetricSelect?: (metricValue: string) => void;
}

// react-markdown strips unknown URL schemes by default, which would drop our xqviz:// / xqsurviz:// /
// xqphoto:// image tags and xqmetric:// metric tags before the renderers below can resolve them.
const urlTransform = (url: string) => (isImageTag(url) || isMetricTag(url) ? url : defaultUrlTransform(url));

const ChatMarkdown = ({ text, metricOptions = [], onMetricSelect }: ChatMarkdownProps) => {
  const components = useMemo(
    (): Components => ({
      img: ({ src, alt }) => <ChatVizImage src={src} alt={alt} />,
      a: ({ href, children, ...props }) =>
        href && isMetricTag(href) ? (
          <>{children}</>
        ) : (
          <a href={href} {...props}>
            {children}
          </a>
        ),
      p: ({ node, children }) => {
        if (onMetricSelect) {
          const metrics = metricsOfTagParagraph(node, metricOptions);
          if (metrics)
            return metrics.length > 0 ? <ChatMetricPills metrics={metrics} onSelect={onMetricSelect} /> : null;
        }
        return <p>{children}</p>;
      },
    }),
    [metricOptions, onMetricSelect]
  );

  const trimmed = text?.trim() ?? '';
  if (!trimmed) {
    return <p className="chat-markdown-empty">No content yet.</p>;
  }
  return (
    <div className="chat-markdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components} urlTransform={urlTransform}>
        {trimmed}
      </ReactMarkdown>
    </div>
  );
};

export default ChatMarkdown;
